import _ from 'lodash'
import React from 'react'
import T from 'prop-types'
import ReactDOM from 'react-dom'
import {
  isNodeInRoot,
  getEventCoordinate,
  getBoundsForNode,
  findAncestorBySelector
} from '../utils'

/*
* Note: heavily based off of https://github.com/unclecheese/react-selectable
* Extended to support usage within react-base scrollable containers
*/

const MIN_DRAG_THRESHOLD = 10

function coordsCollide(
  aTop,
  aLeft,
  bTop,
  bLeft,
  aWidth,
  aHeight,
  bWidth,
  bHeight,
  tolerance
) {
  if (typeof tolerance === 'undefined') {
    tolerance = 0
  }

  return !// 'a' bottom doesn't touch 'b' top
  // 'a' left doesn't touch 'b' right
  (
    aTop + aHeight - tolerance < bTop ||
    // 'a' top doesn't touch 'b' bottom
    aTop + tolerance > bTop + bHeight ||
    // 'a' right doesn't touch 'b' left
    aLeft + aWidth - tolerance < bLeft ||
    aLeft + tolerance > bLeft + bWidth
  )
}

function doObjectsCollide(a, b, tolerance) {
  const aObj = a instanceof HTMLElement ? getBoundsForNode(a) : a
  const bObj = b instanceof HTMLElement ? getBoundsForNode(b) : b

  return coordsCollide(
    aObj.top,
    aObj.left,
    bObj.top,
    bObj.left,
    aObj.offsetWidth,
    aObj.offsetHeight,
    bObj.offsetWidth,
    bObj.offsetHeight,
    tolerance
  )
}

class DragSelectionContainer extends React.Component {
  static displayName = 'DragSelectionContainer'

  static propTypes = {
    className: T.string,
    constrainMouseMoveToContainer: T.bool,
    dragStartSelector: T.string, // If specified, drags will only be allowed to start within elements matching this selector
    enableTouchSupport: T.bool,
    fixedPosition: T.bool,
    onBoundsReached: T.func, // Called when the active selection bounds are nearing a parent's scroll container bounds
    onSelection: T.func,
    onSelectionArgs: T.array,
    requestAutoScroll: T.func, // If defined, will be called to request an autoscroll from the owner component
    scrollLeft: T.number,
    scrollTop: T.number,
    selectOnMouseMove: T.bool,
    tagName: T.node, //
    tagProps: T.object,
    tolerance: T.number,
    coordinateSelection: T.bool,
    preserveSelectionBox: T.bool // If true, selection will not clear/reset the visual selection box on selectionEnd
  }

  static defaultProps = {
    autoscrollRate: 20,
    autoscrollTolerance: 20,
    className: '',
    constrainMouseMoveToContainer: false,
    enableTouchSupport: false, // NOTE can interfere with scrollbars
    tagName: 'div',
    fixedPosition: false,
    scrollLeft: 0,
    scrollTop: 0,
    selectOnMouseMove: false,
    tolerance: 0,
    onSelection: _.noop,
    onSelectionArgs: [],
    coordinateSelection: false,
    preserveSelectionBox: false
  }

  static childContextTypes = {
    dragSelection: T.object
  }

  state = {
    selectionActive: false,
    selectionBounds: {
      top: 0,
      left: 0,
      width: 0,
      height: 0
    }
  }

  getChildContext() {
    return {
      dragSelection: {
        register: this._registerSelectable,
        unregister: this._unregisterSelectable,
        onScrolled: this._onChildScrolled
      }
    }
  }

  UNSAFE_componentWillMount() {
    this._registry = []
    this._latestScrollDims = { scrollTop: 0, scrollLeft: 0 }
    this._mouseDownData = null
    this._throttledSelect = _.debounce(this._selectElements, 50)
  }

  componentDidMount() {
    ReactDOM.findDOMNode(this).addEventListener('mousedown', this._mouseDown)
    if (this.props.enableTouchSupport) {
      ReactDOM.findDOMNode(this).addEventListener('touchstart', this._mouseDown)
    }
  }

  componentWillUnmount() {
    ReactDOM.findDOMNode(this).removeEventListener('mousedown', this._mouseDown)
    if (this.props.enableTouchSupport) {
      ReactDOM.findDOMNode(this).removeEventListener(
        'touchstart',
        this._mouseDown
      )
    }
    this._isUnmounting = true
  }

  _registerSelectable = (key, domNode) => {
    this._registry.push({ key, domNode })
  }

  _unregisterSelectable = key => {
    this._registry = this._registry.filter(data => data.key !== key)
  }

  _registerScrollBars = domNode => {
    domNode.onScrolled = this.onChildScrolled
  }

  _unregisterScrollBars = domNode => {
    delete domNode.onScrolled
  }

  _onChildScrolled = scrollDims => {
    this._latestScrollDims = scrollDims
    if (this._mouseIsDown) {
      this._onDragMouseMove(this._latestMouseMoveEvent)
    }
  }

  _setSelectionBounds = newBounds => {
    this._latestSelectionBoxBounds = newBounds
    if (this._selectionBoxRef) {
      const cssString = _.map(newBounds, (val, prop) => `${prop}: ${val}px`).join(
        '; '
      )
      this._selectionBoxRef.setAttribute('style', cssString)
    }
  }

  _getMouseCoords = e => {
    return {
      mouseX: getEventCoordinate(e, 'pageX'),
      mouseY: getEventCoordinate(e, 'pageY')
    }
  }

  /**
  * Called while moving the mouse with the button down. Changes the boundaries
  * of the selection box
  */
  _onDragMouseMove = e => {
    this._latestMouseMoveEvent = e
    const { mouseX, mouseY } = this._getMouseCoords(e)
    let { initialMouseX, initialMouseY } = this._mouseDownData

    if (
      Math.abs(mouseX - initialMouseX) < MIN_DRAG_THRESHOLD &&
      Math.abs(mouseY - initialMouseY) < MIN_DRAG_THRESHOLD
    ) {
      // Ignore mousemoves until beyond selection threshold
      return
    }

    const scrollTopDiff =
      this._latestScrollDims.scrollTop - this._mouseDownData.initialScrollTop
    const scrollLeftDiff =
      this._latestScrollDims.scrollLeft - this._mouseDownData.initialScrollLeft

    // Initial point should be "stuck" to its position within the scrollable area
    initialMouseY -= scrollTopDiff
    initialMouseX -= scrollLeftDiff

    const topY = Math.max(
      Math.min(mouseY, initialMouseY),
      this._boundingBox.top - 1
    )
    const bottomY = Math.min(
      Math.max(mouseY, initialMouseY),
      this._boundingBox.top + this._boundingBox.offsetHeight + 1
    )
    const leftX = Math.max(
      Math.min(mouseX, initialMouseX),
      this._boundingBox.left - 1
    )
    const rightX = Math.min(
      Math.max(mouseX, initialMouseX),
      this._boundingBox.left + this._boundingBox.offsetWidth + 1
    )

    if (!this.state.selectionActive) {
      this.setState({
        selectionActive: true
      })
    }

    const newSelectionBounds = {
      width: Math.abs(rightX - leftX),
      height: Math.abs(bottomY - topY),
      left: leftX - this._boundingBox.left,
      top: topY - this._boundingBox.top
    }

    this._setSelectionBounds(newSelectionBounds)

    this._throttledSelect(e)
  }

  /**
  * Called when a user presses the mouse button. Determines if a select box should
  * be added, and if so, attach event listeners
  */
  _mouseDown = e => {
    let targetNode = e.target
    // If target (or any parent element) is draggable via html5 `draggable` or react-dnd,
    // OR if mouseDown occurred on pw ScrollBars thumb,
    // OR on an element with a `prevent_dragselection` class,
    // OR a `dragStartSelector` is defined and the target doesn't match it,
    // prevent this drag selection behavior
    if (
      findAncestorBySelector(targetNode, '.pw_scroll_thumb') ||
      findAncestorBySelector(targetNode, '.prevent_dragselection') ||
      (this.props.dragStartSelector && !findAncestorBySelector(targetNode, this.props.dragStartSelector))
    ) {
      return
    }
    while (targetNode) {
      if (targetNode.draggable) {
        return
      }
      targetNode = targetNode.parentNode
    }

    this._mouseIsDown = true

    this._currentSelectedItems = [] // Reset

    const node = ReactDOM.findDOMNode(this)

    let collides, offsetData

    if (this.props.constrainMouseMoveToContainer) {
      ReactDOM.findDOMNode(this).addEventListener('mouseup', this._mouseUp)
      if (this.props.enableTouchSupport) {
        ReactDOM.findDOMNode(this).addEventListener('touchend', this._mouseUp)
      }
    } else {
      window.document.addEventListener('mouseup', this._mouseUp)
      if (this.props.enableTouchSupport) {
        window.document.addEventListener('touchend', this._mouseUp)
      }
    }

    // Right clicks
    if (e.which === 3 || e.button === 2) {
      return
    }

    this._boundingBox = offsetData = getBoundsForNode(node)

    if (!isNodeInRoot(e.target, node)) {
      offsetData = getBoundsForNode(node)

      collides = doObjectsCollide(
        {
          top: offsetData.top,
          left: offsetData.left,
          offsetWidth: offsetData.offsetWidth,
          offsetHeight: offsetData.offsetHeight
        },
        {
          top: getEventCoordinate(e, 'pageY'),
          left: getEventCoordinate(e, 'pageX'),
          offsetWidth: 0,
          offsetHeight: 0
        }
      )
      if (!collides) {
        return
      }
    }

    // If we got this far it's a valid selection drag start point.

    // Prevent native text selection if we're handling the drag
    e.preventDefault()

    const { mouseX, mouseY } = this._getMouseCoords(e)

    this._mouseDownData = {
      initialMouseX: mouseX,
      initialMouseY: mouseY,
      initialScrollTop: this._latestScrollDims.scrollTop,
      initialScrollLeft: this._latestScrollDims.scrollLeft
    }

    if (this.props.constrainMouseMoveToContainer) {
      ReactDOM.findDOMNode(this).addEventListener(
        'mousemove',
        this._onDragMouseMove
      )
      ReactDOM.findDOMNode(this).addEventListener(
        'touchmove',
        this._onDragMouseMove
      )
    } else {
      window.document.addEventListener('mousemove', this._onDragMouseMove)
      window.document.addEventListener('touchmove', this._onDragMouseMove)
    }
  }

  /**
  * Called when the user has completed selection
  */
  _mouseUp = e => {
    this._mouseIsDown = false

    if (this.props.constrainMouseMoveToContainer) {
      ReactDOM.findDOMNode(this).removeEventListener(
        'mousemove',
        this._onDragMouseMove
      )
      ReactDOM.findDOMNode(this).removeEventListener('mouseup', this._mouseUp)
      if (this.props.enableTouchSupport) {
        ReactDOM.findDOMNode(this).removeEventListener(
          'touchmove',
          this._onDragMouseMove
        )
        ReactDOM.findDOMNode(this).removeEventListener(
          'touchend',
          this._mouseUp
        )
      }
    } else {
      window.document.removeEventListener('mousemove', this._onDragMouseMove)
      window.document.removeEventListener('mouseup', this._mouseUp)
      if (this.props.enableTouchSupport) {
        window.document.removeEventListener('touchmove', this._onDragMouseMove)
        window.document.removeEventListener('touchend', this._mouseUp)
      }
    }

    if (!this._mouseDownData) {
      return
    }

    const { initialMouseX, initialMouseY } = this._mouseDownData
    const { mouseX, mouseY } = this._getMouseCoords(e)

    if (
      Math.abs(mouseX - initialMouseX) >= MIN_DRAG_THRESHOLD ||
      Math.abs(mouseY - initialMouseY) >= MIN_DRAG_THRESHOLD
    ) {
      e.preventDefault()

      if (this.props.coordinateSelection) {
        this._emitSelection({
          startX: initialMouseX,
          startY: initialMouseY,
          endX: mouseX,
          endY: mouseY,
          boundary: this._boundingBox
        })
      }
      else {
        this._selectElements(e, true)
      }
    }

    if (!this.props.preserveSelectionBox) {
      this.resetSelectionBox()
    }
  }

  resetSelectionBox = () => {
    this._currentSelectedItems = [] // Reset
    this._mouseDownData = null
    this.setState({
      selectionActive: false
    })
    this._setSelectionBounds({
      width: 0,
      height: 0,
      left: 0,
      top: 0
    })
  }

  /**
  * Selects multiple children given x/y coords of the mouse
  */
  _selectElements = (e, forceTrigger) => {
    const { tolerance } = this.props

    if (!this._selectionBoxRef) {
      return
    }

    const visibleItems = []

    // First prefilter to only compare against visible children
    for (let i = 0, numItems = this._registry.length; i < numItems; i++) {
      const itemData = this._registry[i]
      // Only loop visible items (allow those outside of scrolled bounds to maintain
      // their previous selection state (e.g. for buffered lists that will lose
      // those DOM nodes)

      if (
        itemData.domNode &&
        doObjectsCollide(this._containerRef, itemData.domNode, tolerance)
      ) {
        visibleItems.push(itemData)
      }
    }

    // Second filter for visible children within the selection range
    for (let j = 0, visLen = visibleItems.length; j < visLen; j++) {
      const itemDataJ = visibleItems[j]

      const prevIdx = this._currentSelectedItems.indexOf(itemDataJ.key)

      if (prevIdx > -1) {
        _.pullAt(this._currentSelectedItems, prevIdx) // Reset
      }

      if (
        itemDataJ.domNode &&
        doObjectsCollide(this._selectionBoxRef, itemDataJ.domNode, tolerance)
      ) {
        // Item is contained in selection box
        this._currentSelectedItems.push(itemDataJ.key)
      }
    }

    if (forceTrigger) {
      this._emitSelection(this._currentSelectedItems, e.shiftKey, e)
    } else {
      this._throttledSelectionCb =
        this._throttledSelectionCb ||
        _.debounce((shiftKeyPressed, e2) => {
          if (
            (this.props.selectOnMouseMove && this._mouseIsDown) ||
            forceTrigger
          ) {
            this._emitSelection(
              this._currentSelectedItems,
              shiftKeyPressed,
              e2
            )
          }
        }, 100)
      this._throttledSelectionCb(e.shiftKey, e)
    }
  }

  _emitSelection = (selection, shiftKeyHeld, e) => {
    const {
      onSelection,
      onSelectionArgs
    } = this.props

    onSelection(selection, shiftKeyHeld, e, ...onSelectionArgs)
  }

  _onSelectionBoxRef = el => {
    if (!this._isUnmounting) {
      this._selectionBoxRef = el
    }
  }

  _onContainerRef = el => {
    if (!this._isUnmounting) {
      this._containerRef = el
    }
  }

  render() {
    const { props, state } = this

    return (
      <props.tagName
        className={`drag_selection_container ${props.className} ${state.selectionActive
          ? 'selection_active'
          : ''}`}
        ref={this._onContainerRef}
      >
        {state.selectionActive
          ? <div
              className={`drag_selection_box ${this.props.fixedPosition
                ? 'pos_fixed'
                : ''}`}
              ref={this._onSelectionBoxRef}
            />
          : null}
        {props.children}
      </props.tagName>
    )
  }
}

/*
* Drag selection container/wrapper
*/

export default DragSelectionContainer
